import { EntityType } from '@frontend/common';
import { Logger } from '@frontend/logger';
import { useAccountRepository } from '@frontend/repository';
import { SettingClient } from '@frontend/setting/api';
import { SettingKey } from '@frontend/setting/types';
import { ToastUtil } from '@frontend/toast-utils';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

export interface BackupNotificationUserCardProps {
    entity_id: string;
}

interface ViewProps {
    backupAddressEnabled: boolean | null;
    backupUserId: string | null;
    isLoading: boolean;
    isSaving: boolean;
    updateBackupUser: (userId: string | null) => Promise<void>;
}

export const useBackupNotificationUserCard = (props: BackupNotificationUserCardProps): ViewProps => {
    const { currentAccount } = useAccountRepository();
    const [backupAddressEnabled, setBackupAddressEnabled] = useState<boolean | null>(null);
    const [backupUserId, setBackupUserId] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        SettingClient.fetchSettings({
            key: SettingKey.BACKUP_NOTIFICATION_ADDRESS_ENABLED,
            entity_type: EntityType.ACCOUNT,
            entity_id: currentAccount
        }).then((res) => {
            if (res.results.length > 0 && typeof res.results[0].value === 'boolean') {
                setBackupAddressEnabled(res.results[0].value);
            } else {
                setBackupAddressEnabled(null);
            }
        });
    }, []);

    const findBackupSetting = async (accountId: string, userId: string) => {
        const res = await SettingClient.fetchSettings({
            account_id: accountId,
            key: SettingKey.BACKUP_NOTIFICATION_USER_ID,
            entity_type: EntityType.USER,
            entity_id: userId
        });
        return res.results[0] || null;
    };

    const fetchBackupUser = async () => {
        if (!currentAccount) return;

        setIsLoading(true);
        try {
            const found = await findBackupSetting(currentAccount, props.entity_id);
            if (found && found.value) {
                const value = typeof found.value === 'string' ? found.value : String(found.value);
                setBackupUserId(value);
            } else {
                setBackupUserId(null);
            }
        } catch (error) {
            Logger.error('Failed to fetch backup notification user:', error);
            setBackupUserId(null);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchBackupUser();
    }, [props.entity_id, currentAccount]);

    const updateBackupUser = async (userId: string | null) => {
        if (!currentAccount || isSaving) return;

        setIsSaving(true);
        try {
            const found = await findBackupSetting(currentAccount, props.entity_id);
            let changesMade = false;

            if (userId) {
                if (found) {
                    await SettingClient.patchSetting(currentAccount, found.id, { value: userId });
                } else {
                    await SettingClient.postSetting(currentAccount, {
                        key: SettingKey.BACKUP_NOTIFICATION_USER_ID,
                        value: userId,
                        entity_type: EntityType.USER,
                        entity_id: props.entity_id
                    });
                }
                changesMade = true;
            } else if (found) {
                // Delete the setting if userId is null and setting exists
                await SettingClient.deleteSetting(currentAccount, found.id);
                changesMade = true;
            }

            setBackupUserId(userId);

            // Only show success message if changes were actually made
            if (changesMade) {
                ToastUtil.success(
                    <FormattedMessage
                        id='backup-notification-user-card.success'
                        defaultMessage='Successfully updated backup notification user.'
                    />
                );
            }
        } catch (error) {
            Logger.error('Failed to update backup notification user:', error);
            ToastUtil.error(
                <FormattedMessage
                    id='backup-notification-user-card.error'
                    defaultMessage='Failed to update backup notification user. Please try again.'
                />
            );
        } finally {
            setIsSaving(false);
        }
    };

    return {
        backupAddressEnabled,
        backupUserId,
        isLoading,
        isSaving,
        updateBackupUser
    };
};
